import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { quoteAPI, companyAPI } from '../utils/api';
import Seo from '../components/Seo';
import { useToast } from '../hooks/use-toast';
import { CheckCircle, Building, Mail, Phone, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QuoteRequestPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', service_type: '', message: ''
  });

  useEffect(() => {
    companyAPI.getInfo().then((res) => setCompanyInfo(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let captchaToken = null;
      
      if (window.grecaptcha && window.grecaptcha.execute) {
        try {
          captchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'submit'});
          console.log('Captcha token generated');
        } catch (e) {
          console.warn('Captcha failed, continuing without');
        }
      }
      
      const submitData = captchaToken ? {...formData, captcha_token: captchaToken} : formData;
      await quoteAPI.create(submitData);
      
      setSubmitted(true);
      toast({ title: 'Quote Request Submitted!', description: 'Our team will contact you within 24 hours.' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Request Submitted!</h2>
            <p className="text-gray-600 mb-8">Thank you for your interest. Our team will review your requirements and get back to you within 24 hours.</p>
            <div className="flex space-x-4 justify-center">
              <Button onClick={() => navigate('/')}>Back to Home</Button>
              <Button variant="outline" onClick={() => setSubmitted(false)}>Submit Another</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        pageKey="quote"
        seo={companyInfo?.seo}
        defaults={{
          title: 'Request a Quote | Corporate & Intercity Cab Booking – Ecoway Cabs',
          description: 'Get a free, no-obligation quote for corporate transportation, employee commutes, intercity trips, and airport transfers from Ecoway Cabs. Fast response within 24 hours.',
          keywords: 'cab quote, corporate transportation quote, intercity travel booking, employee transport pricing, airport transfer quote',
        }}
        path="/quote"
      />
      <section className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Request a Quote</h1>
          <p className="text-xl text-blue-100">Get a customized transportation solution for your business</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Fill in Your Details</CardTitle>
              <p className="text-gray-600">We'll get back to you within 24 hours with a detailed quote</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name"><User className="inline h-4 w-4 mr-1" />Full Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label htmlFor="email"><Mail className="inline h-4 w-4 mr-1" />Email *</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone"><Phone className="inline h-4 w-4 mr-1" />Phone *</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required />
                  </div>
                  <div>
                    <Label htmlFor="company"><Building className="inline h-4 w-4 mr-1" />Company</Label>
                    <Input id="company" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} />
                  </div>
                </div>

                <div>
                  <Label>Service Type *</Label>
                  <Select value={formData.service_type} onValueChange={(value) => setFormData({...formData, service_type: value})} required>
                    <SelectTrigger><SelectValue placeholder="Select service type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employee Transportation">Employee Transportation</SelectItem>
                      <SelectItem value="Intercity Travel">Intercity Travel</SelectItem>
                      <SelectItem value="Airport Transfer">Airport & Railway Transfer</SelectItem>
                      <SelectItem value="Full Day Rental">Full Day/Half Day Rental</SelectItem>
                      <SelectItem value="Intracity Travel">Intracity Travel</SelectItem>
                      <SelectItem value="Corporate Solution">Corporate Solutions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Additional Details *</Label>
                  <Textarea id="message" rows={6} placeholder="Tell us about your requirements..." value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required />
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
                
                <p className="text-xs text-center text-gray-500 mt-2">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>
                  {' '}and{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a> apply.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default QuoteRequestPage;
